import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Save, X, Plus, Sparkles, ChevronDown, ChevronRight,
  Target, Heart, Users, Coffee, Star, Clock, BookOpen,
  Building, CheckCircle2, Loader2, Download, Pencil,
  Briefcase, TrendingUp, Globe, Shield, Award, Compass,
  Utensils, Thermometer, Package, MapPin, GraduationCap,
  UserCheck, ClipboardList, Lightbulb, Handshake, Megaphone
} from "lucide-react";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comprehensive Business Bible Sections
const SECTIONS = [
  {
    id: "identity",
    title: "Our Identity",
    subtitle: "Who We Are",
    icon: Compass,
    color: "from-blue-500 to-indigo-600",
    description: "The foundation of who we are as a company",
    fields: [
      { key: "mission_statement", label: "Mission Statement", type: "textarea", aiEnabled: true, placeholder: "Our reason for existing - why we get up every day" },
      { key: "vision_statement", label: "Vision Statement", type: "textarea", aiEnabled: true, placeholder: "The future we're building towards - our North Star" },
      { key: "brand_story", label: "Our Story", type: "textarea", aiEnabled: true, placeholder: "How we started, what drives us, our journey..." },
      { key: "core_values", label: "Core Values", type: "values", aiEnabled: true },
      { key: "culture_principles", label: "Culture Principles", type: "principles", aiEnabled: true },
      { key: "leadership_philosophy", label: "Leadership Philosophy", type: "textarea", aiEnabled: true, placeholder: "How we lead, make decisions, and empower our teams" },
    ]
  },
  {
    id: "purpose",
    title: "Our Purpose",
    subtitle: "Why We Exist",
    icon: Heart,
    color: "from-rose-500 to-pink-600",
    description: "The deeper meaning behind everything we do",
    fields: [
      { key: "brand_promise", label: "Our Promise", type: "textarea", aiEnabled: true, placeholder: "What every customer, team member, and community can count on" },
      { key: "impact_statement", label: "Impact & Legacy", type: "textarea", aiEnabled: true, placeholder: "The lasting change we want to create in the world" },
      { key: "community_commitment", label: "Community Commitment", type: "textarea", aiEnabled: true, placeholder: "How we show up for our neighborhoods and communities" },
      { key: "sustainability_pledge", label: "Sustainability & Ethics", type: "textarea", aiEnabled: true, placeholder: "Our environmental and ethical commitments" },
    ]
  },
  {
    id: "product",
    title: "Our Product",
    subtitle: "What We Offer",
    icon: Coffee,
    color: "from-amber-500 to-orange-600",
    description: "The craft and quality of what we serve",
    fields: [
      { key: "product_philosophy", label: "Product Philosophy", type: "textarea", aiEnabled: true, placeholder: "Our approach to what we create and serve" },
      { key: "sourcing_standards", label: "Sourcing Standards", type: "textarea", aiEnabled: true, placeholder: "Where we source from, quality requirements, ethical standards" },
      { key: "quality_non_negotiables", label: "Quality Non-Negotiables", type: "list", placeholder: "Standards we never compromise on" },
      { key: "menu_philosophy", label: "Menu Philosophy", type: "textarea", aiEnabled: true, placeholder: "How we design our menu, seasonal approach, innovation" },
      { key: "recipe_standards", label: "Recipe & Preparation Standards", type: "textarea", placeholder: "How every item must be prepared, tolerances, consistency" },
      { key: "product_innovation", label: "Innovation Process", type: "textarea", aiEnabled: true, placeholder: "How we develop new products, test, and roll out" },
    ]
  },
  {
    id: "experience",
    title: "Our Experience",
    subtitle: "How We Serve",
    icon: Star,
    color: "from-purple-500 to-violet-600",
    description: "The customer journey and service standards",
    fields: [
      { key: "experience_vision", label: "Experience Vision", type: "textarea", aiEnabled: true, placeholder: "What every customer should feel when they interact with us" },
      { key: "service_philosophy", label: "Service Philosophy", type: "textarea", aiEnabled: true, placeholder: "Our approach to hospitality and customer care" },
      { key: "customer_journey", label: "Customer Journey Standards", type: "journey", aiEnabled: true },
      { key: "atmosphere_standards", label: "Atmosphere & Ambiance", type: "textarea", aiEnabled: true, placeholder: "Music, lighting, scent, temperature, cleanliness expectations" },
      { key: "problem_resolution", label: "Problem Resolution", type: "textarea", aiEnabled: true, placeholder: "How we handle complaints, empowerment levels, recovery standards" },
      { key: "experience_metrics", label: "Experience Metrics", type: "list", placeholder: "How we measure customer satisfaction" },
    ]
  },
  {
    id: "operations",
    title: "Our Operations",
    subtitle: "How We Run",
    icon: ClipboardList,
    color: "from-emerald-500 to-teal-600",
    description: "Daily operations and store management standards",
    fields: [
      { key: "operations_philosophy", label: "Operations Philosophy", type: "textarea", aiEnabled: true, placeholder: "Our approach to running efficient, consistent stores" },
      { key: "daily_rhythms", label: "Daily Rhythms & Routines", type: "textarea", placeholder: "Opening, peak hours, transitions, closing procedures" },
      { key: "shift_standards", label: "Shift Management Standards", type: "textarea", placeholder: "Handoffs, communication, staffing levels" },
      { key: "inventory_management", label: "Inventory Management", type: "textarea", placeholder: "Ordering, par levels, waste management, freshness" },
      { key: "equipment_standards", label: "Equipment Standards", type: "textarea", placeholder: "Maintenance schedules, calibration, replacement criteria" },
      { key: "health_safety", label: "Health & Safety Standards", type: "list", placeholder: "Non-negotiable safety requirements" },
      { key: "compliance_requirements", label: "Compliance Requirements", type: "textarea", placeholder: "Regulatory requirements, certifications, inspections" },
    ]
  },
  {
    id: "people",
    title: "Our People",
    subtitle: "How We Grow Teams",
    icon: Users,
    color: "from-cyan-500 to-blue-600",
    description: "Hiring, development, and team culture",
    fields: [
      { key: "people_philosophy", label: "People Philosophy", type: "textarea", aiEnabled: true, placeholder: "How we view our team members, what we believe about people" },
      { key: "hiring_standards", label: "Hiring Standards", type: "textarea", aiEnabled: true, placeholder: "Who we look for, interview process, must-haves vs nice-to-haves" },
      { key: "ideal_team_member", label: "Ideal Team Member Profile", type: "personas", aiEnabled: true },
      { key: "onboarding_journey", label: "Onboarding Journey", type: "textarea", placeholder: "First day, first week, first month, first quarter" },
      { key: "training_philosophy", label: "Training Philosophy", type: "textarea", aiEnabled: true, placeholder: "How we develop skills, certifications, ongoing learning" },
      { key: "career_pathways", label: "Career Pathways", type: "textarea", aiEnabled: true, placeholder: "Growth opportunities, promotion criteria, leadership development" },
      { key: "performance_standards", label: "Performance Standards", type: "textarea", placeholder: "How we measure success, feedback cadence, accountability" },
      { key: "recognition_culture", label: "Recognition & Rewards", type: "textarea", aiEnabled: true, placeholder: "How we celebrate wins, recognition programs, incentives" },
    ]
  },
  {
    id: "standards",
    title: "Our Standards",
    subtitle: "Quality & Consistency",
    icon: Award,
    color: "from-yellow-500 to-amber-600",
    description: "The non-negotiables that define our quality",
    fields: [
      { key: "quality_philosophy", label: "Quality Philosophy", type: "textarea", aiEnabled: true, placeholder: "What quality means to us and why it matters" },
      { key: "presentation_standards", label: "Presentation Standards", type: "textarea", placeholder: "How every drink, food item, and package should look" },
      { key: "cleanliness_standards", label: "Cleanliness Standards", type: "list", placeholder: "Cleaning schedules, inspection criteria" },
      { key: "uniform_standards", label: "Uniform & Appearance", type: "textarea", placeholder: "Dress code, grooming, name badges, PPE" },
      { key: "communication_standards", label: "Communication Standards", type: "textarea", placeholder: "How we speak to customers, phone etiquette, written communication" },
      { key: "audit_criteria", label: "Store Audit Criteria", type: "list", placeholder: "What we measure in store audits" },
    ]
  },
  {
    id: "growth",
    title: "Our Growth",
    subtitle: "How We Expand",
    icon: TrendingUp,
    color: "from-green-500 to-emerald-600",
    description: "New store development and scaling standards",
    fields: [
      { key: "growth_philosophy", label: "Growth Philosophy", type: "textarea", aiEnabled: true, placeholder: "How we think about expansion - quality over speed, strategic growth" },
      { key: "site_selection", label: "Site Selection Criteria", type: "list", placeholder: "Location requirements, demographics, competition analysis" },
      { key: "store_formats", label: "Store Formats", type: "textarea", placeholder: "Different store types, sizes, and their requirements" },
      { key: "buildout_standards", label: "Build-Out Standards", type: "textarea", placeholder: "Design standards, equipment specs, timeline expectations" },
      { key: "launch_playbook", label: "New Store Launch Playbook", type: "textarea", aiEnabled: true, placeholder: "Pre-opening checklist, soft opening, grand opening, first 90 days" },
      { key: "market_entry", label: "Market Entry Strategy", type: "textarea", aiEnabled: true, placeholder: "How we enter new markets, local adaptation, community integration" },
      { key: "expansion_timeline", label: "Expansion Milestones", type: "textarea", placeholder: "Store count targets, geographic priorities, timeline" },
    ]
  },
  {
    id: "brand",
    title: "Our Brand",
    subtitle: "Visual & Voice",
    icon: Megaphone,
    color: "from-pink-500 to-rose-600",
    description: "How we look, sound, and communicate",
    fields: [
      { key: "brand_personality", label: "Brand Personality", type: "personality", aiEnabled: true },
      { key: "tone_of_voice", label: "Tone of Voice", type: "textarea", aiEnabled: true, placeholder: "How we speak - warm/professional, casual/formal, etc." },
      { key: "visual_identity", label: "Visual Identity Summary", type: "textarea", placeholder: "Logo usage, colors, typography essentials" },
      { key: "colors", label: "Brand Colors", type: "colors" },
      { key: "taglines", label: "Taglines & Key Messages", type: "taglines", aiEnabled: true },
      { key: "photography_style", label: "Photography & Imagery", type: "textarea", aiEnabled: true, placeholder: "Style guidelines for photos, social media, marketing" },
      { key: "marketing_principles", label: "Marketing Principles", type: "list", placeholder: "How we market ourselves, what we never do" },
    ]
  },
  {
    id: "community",
    title: "Our Community",
    subtitle: "Local & Global Impact",
    icon: Globe,
    color: "from-indigo-500 to-purple-600",
    description: "How we connect with and give back to communities",
    fields: [
      { key: "community_philosophy", label: "Community Philosophy", type: "textarea", aiEnabled: true, placeholder: "What community means to us and our role in it" },
      { key: "local_integration", label: "Local Integration", type: "textarea", aiEnabled: true, placeholder: "How each store becomes part of its neighborhood" },
      { key: "partnership_standards", label: "Partnership Standards", type: "textarea", placeholder: "Who we partner with, criteria, types of partnerships" },
      { key: "giving_back", label: "Giving Back Programs", type: "textarea", placeholder: "Donations, volunteering, community support programs" },
      { key: "events_activations", label: "Events & Activations", type: "textarea", aiEnabled: true, placeholder: "Types of events we host, community gatherings, celebrations" },
    ]
  },
  {
    id: "rules",
    title: "Golden Rules",
    subtitle: "Do's & Don'ts",
    icon: Shield,
    color: "from-red-500 to-orange-600",
    description: "The absolute rules that guide everything",
    fields: [
      { key: "do_list", label: "Always Do", type: "list", placeholder: "Things we always do, no matter what" },
      { key: "dont_list", label: "Never Do", type: "list", placeholder: "Lines we never cross" },
      { key: "decision_framework", label: "Decision Framework", type: "textarea", aiEnabled: true, placeholder: "When in doubt, how do we decide? What questions to ask?" },
      { key: "escalation_guide", label: "Escalation Guide", type: "textarea", placeholder: "What needs to be escalated, to whom, how quickly" },
      { key: "notes", label: "Additional Notes", type: "textarea", placeholder: "Any other critical guidance" },
    ]
  },
];

// Inline Editable Text Component
function InlineEdit({ value, onChange, placeholder, multiline = false, className = "" }) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = () => {
    onChange(tempValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(value || "");
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="relative">
        {multiline ? (
          <Textarea
            ref={inputRef}
            value={tempValue}
            onChange={e => setTempValue(e.target.value)}
            className={`min-h-[100px] text-sm pr-20 ${className}`}
            onKeyDown={e => { if (e.key === "Escape") handleCancel(); }}
          />
        ) : (
          <Input
            ref={inputRef}
            value={tempValue}
            onChange={e => setTempValue(e.target.value)}
            className={`text-sm pr-20 ${className}`}
            onKeyDown={e => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") handleCancel();
            }}
          />
        )}
        <div className="absolute right-2 top-2 flex gap-1">
          <button onClick={handleSave} className="p-1 bg-emerald-100 rounded hover:bg-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          </button>
          <button onClick={handleCancel} className="p-1 bg-stone-100 rounded hover:bg-stone-200">
            <X className="w-3.5 h-3.5 text-stone-600" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`group cursor-pointer p-3 -m-1 rounded-lg hover:bg-stone-50 border border-transparent hover:border-stone-200 transition-all ${className}`}
      onClick={() => { setTempValue(value || ""); setIsEditing(true); }}
    >
      {value ? (
        <span className="text-sm text-stone-700 whitespace-pre-wrap">{value}</span>
      ) : (
        <span className="text-sm text-stone-400 italic">{placeholder || "Click to add content..."}</span>
      )}
      <Pencil className="w-3 h-3 text-stone-400 inline ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

// Section Component
function Section({ section, form, setForm, onGenerate, generating, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = section.icon;

  // Calculate completion
  const filledFields = section.fields.filter(f => {
    const val = form[f.key];
    if (Array.isArray(val)) return val.length > 0;
    return val && val.trim();
  }).length;
  const completion = Math.round((filledFields / section.fields.length) * 100);

  return (
    <Card className="bg-white border border-stone-200/60 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div 
        className={`flex items-center gap-4 px-6 py-5 cursor-pointer transition-colors ${expanded ? 'bg-stone-50' : 'hover:bg-stone-50/50'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`p-3 rounded-xl bg-gradient-to-br ${section.color} shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-stone-900">{section.title}</h3>
            <span className="text-xs text-stone-400">•</span>
            <span className="text-sm text-stone-500">{section.subtitle}</span>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">{section.description}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-semibold text-stone-700">{completion}%</div>
            <div className="text-[10px] text-stone-400">{filledFields}/{section.fields.length} fields</div>
          </div>
          <div className="w-16 h-2 bg-stone-100 rounded-full overflow-hidden">
            <div className={`h-full bg-gradient-to-r ${section.color} transition-all`} style={{ width: `${completion}%` }} />
          </div>
          {expanded ? <ChevronDown className="w-5 h-5 text-stone-400" /> : <ChevronRight className="w-5 h-5 text-stone-400" />}
        </div>
      </div>
      
      {expanded && (
        <CardContent className="px-6 py-6 space-y-6 border-t border-stone-100">
          {section.fields.map(field => (
            <FieldRenderer 
              key={field.key}
              field={field}
              value={form[field.key]}
              onChange={(val) => setForm(f => ({ ...f, [field.key]: val }))}
              onGenerate={() => onGenerate(field.key)}
              generating={generating === field.key}
              onSave={onSave}
            />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

// Field Renderer
function FieldRenderer({ field, value, onChange, onGenerate, generating, onSave }) {
  const [newItem, setNewItem] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newValue, setNewValue] = useState({ name: "", description: "" });
  const [newColor, setNewColor] = useState({ name: "", hex: "#000000", usage: "", type: "primary" });
  const [newTrait, setNewTrait] = useState({ trait: "", description: "" });
  const [newPersona, setNewPersona] = useState({ name: "", description: "", demographics: "", needs: "", pain_points: "" });
  const [newJourney, setNewJourney] = useState({ stage: "", standard: "" });
  const [newPrinciple, setNewPrinciple] = useState({ title: "", description: "" });

  // Textarea
  if (field.type === "textarea") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-stone-800">{field.label}</label>
          {field.aiEnabled && (
            <Button size="sm" variant="outline" onClick={onGenerate} disabled={generating} className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              {generating ? "Generating..." : "AI Generate"}
            </Button>
          )}
        </div>
        <InlineEdit value={value} onChange={(val) => { onChange(val); onSave(); }} placeholder={field.placeholder} multiline={true} />
      </div>
    );
  }

  // List
  if (field.type === "list" || field.type === "taglines") {
    const items = value || [];
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-stone-800">{field.label}</label>
          {field.aiEnabled && (
            <Button size="sm" variant="outline" onClick={onGenerate} disabled={generating} className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Generate
            </Button>
          )}
        </div>
        <div className="space-y-1.5">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2 p-2.5 bg-stone-50 rounded-lg group hover:bg-stone-100 transition-colors">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="text-sm text-stone-700 flex-1">{item}</span>
              <button onClick={() => { onChange(items.filter((_, idx) => idx !== i)); onSave(); }} className="opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-4 h-4 text-red-400 hover:text-red-600" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder={field.placeholder} className="text-sm"
            onKeyDown={e => { if (e.key === "Enter" && newItem.trim()) { onChange([...items, newItem.trim()]); setNewItem(""); onSave(); }}} />
          <Button variant="outline" onClick={() => { if (newItem.trim()) { onChange([...items, newItem.trim()]); setNewItem(""); onSave(); }}}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Values
  if (field.type === "values") {
    const values = value || [];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-stone-800">{field.label}</label>
          {field.aiEnabled && (
            <Button size="sm" variant="outline" onClick={onGenerate} disabled={generating} className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Generate Values
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {values.map((v, i) => (
            <div key={i} className="p-4 bg-gradient-to-br from-rose-50 to-orange-50 rounded-xl border border-rose-100 group relative">
              <div className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-stone-900">{v.name}</p>
                  <p className="text-sm text-stone-600 mt-1">{v.description}</p>
                </div>
              </div>
              <button onClick={() => { onChange(values.filter((_, idx) => idx !== i)); onSave(); }} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-white rounded-full shadow"><X className="w-3 h-3 text-red-400" /></button>
            </div>
          ))}
        </div>
        {showAddForm ? (
          <div className="p-4 bg-stone-50 rounded-xl space-y-3">
            <Input value={newValue.name} onChange={e => setNewValue(v => ({ ...v, name: e.target.value }))} placeholder="Value name (e.g., Excellence)" className="text-sm" />
            <Textarea value={newValue.description} onChange={e => setNewValue(v => ({ ...v, description: e.target.value }))} placeholder="What this value means and how it shows up..." className="text-sm min-h-[80px]" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button onClick={() => { if (newValue.name) { onChange([...values, { ...newValue }]); setNewValue({ name: "", description: "" }); setShowAddForm(false); onSave(); }}} className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-1" /> Add Value</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setShowAddForm(true)} className="w-full"><Plus className="w-4 h-4 mr-1" /> Add Core Value</Button>
        )}
      </div>
    );
  }

  // Culture Principles
  if (field.type === "principles") {
    const principles = value || [];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-stone-800">{field.label}</label>
          {field.aiEnabled && (
            <Button size="sm" variant="outline" onClick={onGenerate} disabled={generating} className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Generate Principles
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {principles.map((p, i) => (
            <div key={i} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 group relative">
              <p className="font-semibold text-stone-900">{p.title}</p>
              <p className="text-sm text-stone-600 mt-1">{p.description}</p>
              <button onClick={() => { onChange(principles.filter((_, idx) => idx !== i)); onSave(); }} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-white rounded-full shadow"><X className="w-3 h-3 text-red-400" /></button>
            </div>
          ))}
        </div>
        {showAddForm ? (
          <div className="p-4 bg-stone-50 rounded-xl space-y-3">
            <Input value={newPrinciple.title} onChange={e => setNewPrinciple(p => ({ ...p, title: e.target.value }))} placeholder="Principle title" className="text-sm" />
            <Textarea value={newPrinciple.description} onChange={e => setNewPrinciple(p => ({ ...p, description: e.target.value }))} placeholder="What this principle means..." className="text-sm min-h-[60px]" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button onClick={() => { if (newPrinciple.title) { onChange([...principles, { ...newPrinciple }]); setNewPrinciple({ title: "", description: "" }); setShowAddForm(false); onSave(); }}} className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setShowAddForm(true)} className="w-full"><Plus className="w-4 h-4 mr-1" /> Add Principle</Button>
        )}
      </div>
    );
  }

  // Customer Journey
  if (field.type === "journey") {
    const stages = value || [];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-stone-800">{field.label}</label>
          {field.aiEnabled && (
            <Button size="sm" variant="outline" onClick={onGenerate} disabled={generating} className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Generate Journey
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {stages.map((s, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl border border-purple-100 group relative">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</div>
              <div>
                <p className="font-semibold text-stone-900">{s.stage}</p>
                <p className="text-sm text-stone-600 mt-0.5">{s.standard}</p>
              </div>
              <button onClick={() => { onChange(stages.filter((_, idx) => idx !== i)); onSave(); }} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-white rounded-full shadow"><X className="w-3 h-3 text-red-400" /></button>
            </div>
          ))}
        </div>
        {showAddForm ? (
          <div className="p-4 bg-stone-50 rounded-xl space-y-3">
            <Input value={newJourney.stage} onChange={e => setNewJourney(j => ({ ...j, stage: e.target.value }))} placeholder="Stage name (e.g., Greeting)" className="text-sm" />
            <Textarea value={newJourney.standard} onChange={e => setNewJourney(j => ({ ...j, standard: e.target.value }))} placeholder="Standard for this stage..." className="text-sm min-h-[60px]" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button onClick={() => { if (newJourney.stage) { onChange([...stages, { ...newJourney }]); setNewJourney({ stage: "", standard: "" }); setShowAddForm(false); onSave(); }}} className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-1" /> Add Stage</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setShowAddForm(true)} className="w-full"><Plus className="w-4 h-4 mr-1" /> Add Journey Stage</Button>
        )}
      </div>
    );
  }

  // Personas (for ideal team member)
  if (field.type === "personas") {
    const personas = value || [];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-stone-800">{field.label}</label>
          {field.aiEnabled && (
            <Button size="sm" variant="outline" onClick={onGenerate} disabled={generating} className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Generate
            </Button>
          )}
        </div>
        <div className="space-y-3">
          {personas.map((p, i) => (
            <div key={i} className="p-4 bg-cyan-50 rounded-xl border border-cyan-100 group relative">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-stone-900">{p.name}</p>
                  <p className="text-sm text-stone-600 mt-1">{p.description}</p>
                  {p.demographics && <p className="text-xs text-stone-500 mt-2"><strong>Profile:</strong> {p.demographics}</p>}
                  {p.needs && <p className="text-xs text-stone-500"><strong>Traits:</strong> {p.needs}</p>}
                  {p.pain_points && <p className="text-xs text-stone-500"><strong>Development Areas:</strong> {p.pain_points}</p>}
                </div>
              </div>
              <button onClick={() => { onChange(personas.filter((_, idx) => idx !== i)); onSave(); }} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-white rounded-full shadow"><X className="w-3 h-3 text-red-400" /></button>
            </div>
          ))}
        </div>
        {showAddForm ? (
          <div className="p-4 bg-stone-50 rounded-xl space-y-2">
            <Input value={newPersona.name} onChange={e => setNewPersona(p => ({ ...p, name: e.target.value }))} placeholder="Role/Profile name" className="text-sm" />
            <Textarea value={newPersona.description} onChange={e => setNewPersona(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="text-sm min-h-[50px]" />
            <Input value={newPersona.demographics} onChange={e => setNewPersona(p => ({ ...p, demographics: e.target.value }))} placeholder="Profile characteristics" className="text-sm" />
            <Input value={newPersona.needs} onChange={e => setNewPersona(p => ({ ...p, needs: e.target.value }))} placeholder="Key traits" className="text-sm" />
            <Input value={newPersona.pain_points} onChange={e => setNewPersona(p => ({ ...p, pain_points: e.target.value }))} placeholder="Development areas" className="text-sm" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button onClick={() => { if (newPersona.name) { onChange([...personas, { ...newPersona }]); setNewPersona({ name: "", description: "", demographics: "", needs: "", pain_points: "" }); setShowAddForm(false); onSave(); }}} className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setShowAddForm(true)} className="w-full"><Plus className="w-4 h-4 mr-1" /> Add Profile</Button>
        )}
      </div>
    );
  }

  // Personality Traits
  if (field.type === "personality") {
    const traits = value || [];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-stone-800">{field.label}</label>
          {field.aiEnabled && (
            <Button size="sm" variant="outline" onClick={onGenerate} disabled={generating} className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Generate
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {traits.map((t, i) => (
            <div key={i} className="px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg border border-pink-100 group relative">
              <p className="font-semibold text-pink-900">{t.trait}</p>
              <p className="text-xs text-pink-700">{t.description}</p>
              <button onClick={() => { onChange(traits.filter((_, idx) => idx !== i)); onSave(); }} className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 p-0.5 bg-white rounded-full shadow"><X className="w-3 h-3 text-red-400" /></button>
            </div>
          ))}
        </div>
        {showAddForm ? (
          <div className="flex gap-2 items-end p-3 bg-stone-50 rounded-lg">
            <Input value={newTrait.trait} onChange={e => setNewTrait(t => ({ ...t, trait: e.target.value }))} placeholder="Trait" className="text-sm flex-1" />
            <Input value={newTrait.description} onChange={e => setNewTrait(t => ({ ...t, description: e.target.value }))} placeholder="Description" className="text-sm flex-1" />
            <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
            <Button onClick={() => { if (newTrait.trait) { onChange([...traits, { ...newTrait }]); setNewTrait({ trait: "", description: "" }); setShowAddForm(false); onSave(); }}}><Plus className="w-4 h-4" /></Button>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setShowAddForm(true)}><Plus className="w-4 h-4 mr-1" /> Add Trait</Button>
        )}
      </div>
    );
  }

  // Colors
  if (field.type === "colors") {
    const colors = value || [];
    return (
      <div className="space-y-3">
        <label className="text-sm font-semibold text-stone-800 block">{field.label}</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {colors.map((c, i) => (
            <div key={i} className="relative group">
              <div className="p-3 bg-white rounded-xl shadow-sm border">
                <div className="w-full h-12 rounded-lg mb-2" style={{ backgroundColor: c.hex }} />
                <p className="text-xs font-semibold text-stone-900">{c.name}</p>
                <p className="text-[10px] text-stone-500 font-mono">{c.hex}</p>
              </div>
              <button onClick={() => { onChange(colors.filter((_, idx) => idx !== i)); onSave(); }} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 bg-white rounded-full shadow"><X className="w-3 h-3 text-red-400" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 items-end p-3 bg-stone-50 rounded-lg">
          <div><label className="text-[10px] text-stone-500">Name</label><Input value={newColor.name} onChange={e => setNewColor(c => ({ ...c, name: e.target.value }))} className="text-xs h-8 w-24" /></div>
          <div><label className="text-[10px] text-stone-500">Color</label><input type="color" value={newColor.hex} onChange={e => setNewColor(c => ({ ...c, hex: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" /></div>
          <Button variant="outline" onClick={() => { if (newColor.name) { onChange([...colors, { ...newColor }]); setNewColor({ name: "", hex: "#000000", usage: "", type: "primary" }); onSave(); }}} className="h-8"><Plus className="w-3 h-3" /></Button>
        </div>
      </div>
    );
  }

  return null;
}

// PDF Export
function exportToPDF(form, companyName = "Business Bible") {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let y = margin;

  const addPage = () => { pdf.addPage(); y = margin; };
  const checkBreak = (space) => { if (y + space > pageHeight - margin) { addPage(); return true; } return false; };
  
  const addTitle = (text, size = 22) => { checkBreak(15); pdf.setFontSize(size); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20); pdf.text(text, margin, y); y += size * 0.5 + 5; };
  const addSubtitle = (text) => { checkBreak(10); pdf.setFontSize(12); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 100, 100); pdf.text(text, margin, y); y += 8; };
  const addLabel = (text) => { checkBreak(10); pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(80, 80, 80); pdf.text(text.toUpperCase(), margin, y); y += 5; };
  const addText = (text, indent = 0) => { if (!text) return; pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(40, 40, 40); const lines = pdf.splitTextToSize(text, contentWidth - indent); lines.forEach(l => { checkBreak(5); pdf.text(l, margin + indent, y); y += 5; }); y += 2; };
  const addListItem = (text, bullet = "•") => { if (!text) return; checkBreak(5); pdf.setFontSize(10); pdf.text(bullet, margin + 3, y); const lines = pdf.splitTextToSize(text, contentWidth - 12); lines.forEach((l, i) => { if (i > 0) checkBreak(5); pdf.text(l, margin + 10, y); y += 5; }); };

  // Cover
  pdf.setFillColor(248, 248, 248);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  pdf.setFontSize(32); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
  pdf.text(companyName, pageWidth / 2, pageHeight / 2 - 25, { align: 'center' });
  pdf.setFontSize(20); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
  pdf.text("Business Bible", pageWidth / 2, pageHeight / 2, { align: 'center' });
  pdf.setFontSize(10);
  pdf.text(`The complete guide to who we are, what we do, and how we do it`, pageWidth / 2, pageHeight / 2 + 15, { align: 'center' });
  pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 25, { align: 'center' });

  // Generate each section
  SECTIONS.forEach(section => {
    addPage();
    addTitle(section.title);
    addSubtitle(section.subtitle + " — " + section.description);
    y += 5;

    section.fields.forEach(field => {
      const val = form[field.key];
      if (!val || (Array.isArray(val) && val.length === 0)) return;
      
      addLabel(field.label);
      
      if (field.type === "textarea") {
        addText(val);
      } else if (field.type === "list" || field.type === "taglines") {
        val.forEach(item => addListItem(item));
      } else if (field.type === "values" || field.type === "principles") {
        val.forEach(v => {
          checkBreak(12);
          pdf.setFont('helvetica', 'bold'); pdf.text(v.name || v.title, margin + 3, y); y += 5;
          pdf.setFont('helvetica', 'normal'); addText(v.description, 3);
        });
      } else if (field.type === "journey") {
        val.forEach((s, i) => {
          checkBreak(12);
          pdf.setFont('helvetica', 'bold'); pdf.text(`${i + 1}. ${s.stage}`, margin + 3, y); y += 5;
          pdf.setFont('helvetica', 'normal'); addText(s.standard, 3);
        });
      } else if (field.type === "personas") {
        val.forEach(p => {
          checkBreak(20);
          pdf.setFont('helvetica', 'bold'); pdf.text(p.name, margin + 3, y); y += 5;
          pdf.setFont('helvetica', 'normal'); addText(p.description, 3);
          if (p.demographics) addText(`Profile: ${p.demographics}`, 3);
          if (p.needs) addText(`Traits: ${p.needs}`, 3);
        });
      } else if (field.type === "personality") {
        val.forEach(t => {
          checkBreak(10);
          pdf.setFont('helvetica', 'bold'); pdf.text(t.trait, margin + 3, y);
          pdf.setFont('helvetica', 'normal'); pdf.text(` — ${t.description}`, margin + 3 + pdf.getTextWidth(t.trait), y); y += 6;
        });
      } else if (field.type === "colors") {
        val.forEach(c => {
          checkBreak(8);
          pdf.setFillColor(parseInt(c.hex.slice(1, 3), 16), parseInt(c.hex.slice(3, 5), 16), parseInt(c.hex.slice(5, 7), 16));
          pdf.rect(margin, y - 4, 6, 6, 'F');
          pdf.text(`${c.name} — ${c.hex}`, margin + 10, y); y += 6;
        });
      }
      y += 3;
    });
  });

  pdf.save(`${companyName.replace(/\s+/g, '_')}_Business_Bible.pdf`);
}

// Main Component
export default function BrandBible() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [exporting, setExporting] = useState(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/marketing/brand-playbook`).then(r => setForm(r.data));
  }, []);

  const save = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/marketing/brand-playbook`, form);
        toast.success("Saved", { duration: 1500 });
      } catch { toast.error("Failed to save"); }
      finally { setSaving(false); }
    }, 1000);
  };

  const handleGenerate = async (section) => {
    setGenerating(section);
    try {
      const res = await axios.post(`${API}/marketing/brand-playbook/generate`, { section, context: {} });
      if (res.data.parsed) setForm(f => ({ ...f, [section]: res.data.parsed }));
      else setForm(f => ({ ...f, [section]: res.data.content }));
      toast.success(`Generated ${section.replace(/_/g, " ")}`);
      save();
    } catch { toast.error("Generation failed"); }
    finally { setGenerating(null); }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const companyRes = await axios.get(`${API}/company`).catch(() => null);
      exportToPDF(form, companyRes?.data?.name || "Business Bible");
      toast.success("PDF exported");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  // Calculate overall completion
  const totalFields = SECTIONS.reduce((acc, s) => acc + s.fields.length, 0);
  const filledFields = SECTIONS.reduce((acc, s) => acc + s.fields.filter(f => {
    const val = form[f.key];
    if (Array.isArray(val)) return val.length > 0;
    return val && val.trim();
  }).length, 0);
  const overallCompletion = Math.round((filledFields / totalFields) * 100);

  return (
    <div className="space-y-8" data-testid="business-bible">
      {/* Header */}
      <div className="bg-gradient-to-r from-stone-900 to-stone-800 rounded-2xl p-8 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Business Bible</h1>
            <p className="text-stone-300 mt-2 max-w-xl">The complete guide to who we are, what we do, why we do it, and how we do it. The holy grail for running every store and ensuring alignment across our entire organization.</p>
          </div>
          <div className="flex items-center gap-4">
            {saving && <span className="text-xs text-stone-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving...</span>}
            <Button onClick={handleExportPDF} disabled={exporting} className="bg-white text-stone-900 hover:bg-stone-100">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>
        </div>
        
        {/* Progress */}
        <div className="mt-6 flex items-center gap-4">
          <div className="flex-1 h-3 bg-stone-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-all" style={{ width: `${overallCompletion}%` }} />
          </div>
          <span className="text-lg font-bold">{overallCompletion}%</span>
          <span className="text-stone-400 text-sm">{filledFields}/{totalFields} fields complete</span>
        </div>
      </div>

      {/* Tips */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-3">
          <Pencil className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900">Click to Edit</p>
            <p className="text-xs text-blue-700">All fields are inline-editable. Click any text to edit. Changes auto-save.</p>
          </div>
        </div>
        <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-purple-900">AI-Powered</p>
            <p className="text-xs text-purple-700">Use "AI Generate" buttons to auto-generate content based on your company context.</p>
          </div>
        </div>
      </div>

      {/* Sections */}
      {SECTIONS.map(section => (
        <Section key={section.id} section={section} form={form} setForm={setForm} onGenerate={handleGenerate} generating={generating} onSave={save} />
      ))}
    </div>
  );
}
